import "./index.css";

// i18n
import "./locales/i18n";

// scroll bar
import "simplebar-react/dist/simplebar.min.css";

// lightbox
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/captions.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

// editor
import "react-quill/dist/quill.snow.css";

// slick-carousel
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// lazy image
import "react-lazy-load-image-component/src/effects/blur.css";

import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import App from "./App";
import { Auth0Provider } from "@auth0/auth0-react";
import { AuthProvider } from "./contexts/AuthContext";
import reportWebVitals from "./reportWebVitals";
import { AppThemeProvider } from "contexts/AppThemeContext";
import { WizardContextProvider } from "contexts/WizardContext";
import { RumPublicApi } from "@datadog/browser-rum-core";
import { LicenseManager as LicenseManagerEnterprise } from "ag-grid-enterprise";
import { LicenseManager as LicenseManagerCore } from "@ag-grid-enterprise/core";

import { ModuleRegistry } from "@ag-grid-community/core";
import { SideBarModule } from "@ag-grid-enterprise/side-bar";
import { ColumnsToolPanelModule } from "@ag-grid-enterprise/column-tool-panel";
import { FiltersToolPanelModule } from "@ag-grid-enterprise/filter-tool-panel";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import { CsvExportModule } from "@ag-grid-community/csv-export";
import { ExcelExportModule } from "@ag-grid-enterprise/excel-export";
import { MasterDetailModule } from "@ag-grid-enterprise/master-detail";
import { ServerSideRowModelModule } from "@ag-grid-enterprise/server-side-row-model";
import { configuredQueryClient } from "configs/react-query.config";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { QueryClientProvider } from "@tanstack/react-query";
import CustomSnackProvider from "components/CustomSnackProvider";
import ThemeProvider from "theme";

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
const domain = process.env.REACT_APP_DOMAIN;
const clientId = process.env.REACT_APP_CLIENT_ID;
const audience = process.env.REACT_APP_AUDIENCE;
const scope = process.env.REACT_APP_SCOPE;
const agLicense = process.env.REACT_APP_AG_GRID_LICENSE;

LicenseManagerEnterprise.setLicenseKey(agLicense ?? "");
LicenseManagerCore.setLicenseKey(agLicense ?? "");

ModuleRegistry.registerModules([
  SideBarModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  ClientSideRowModelModule,
  CsvExportModule,
  ExcelExportModule,
  MasterDetailModule,
  ServerSideRowModelModule,
]);
declare global {
  interface Window {
    DD_RUM?: RumPublicApi & {
      startView?(name?: string): void;
    };
  }
}

root.render(
  <React.StrictMode>
    <Auth0Provider
      domain={domain!}
      clientId={clientId!}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: audience,
        scope: scope,
      }}
    >
      <WizardContextProvider>
        <HelmetProvider>
          <AppThemeProvider>
            <ThemeProvider>
              <CustomSnackProvider>
                <BrowserRouter>
                  <QueryClientProvider client={configuredQueryClient}>
                    <App />
                    <ReactQueryDevtools initialIsOpen={false} />
                  </QueryClientProvider>
                </BrowserRouter>
              </CustomSnackProvider>
            </ThemeProvider>
          </AppThemeProvider>
        </HelmetProvider>
      </WizardContextProvider>
    </Auth0Provider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
